/**
 * Assistant d'échéancier (règles 2026-02).
 *
 * Retourne l'ensemble des schémas autorisés selon le montant total HT.
 *
 * - < 3 000 € → acompte 30% + solde uniquement.
 * - 3 000-6 000 € → au choix : (acompte + solde) OU (acompte + 1 situation + solde).
 * - > 6 000 € → acompte + N situations (N ≥ 1) + solde, N choisi par le donneur d'ordre.
 *
 * Ces règles doivent rester alignées avec le validator Pydantic
 * ``BesoinIn._validate_echeancier_tiers`` côté serveur.
 */

export const ACOMPTE_RATE = 0.30;
export const TIER_1 = 3000;
export const TIER_2 = 6000;

export function tierFor(total) {
  if (!(total > 0)) return null;
  if (total < TIER_1) return 1;
  if (total <= TIER_2) return 2;
  return 3;
}

/** Retourne les modes autorisés pour un montant HT donné.
 * Chaque mode expose { id, label, situations: N }. */
export function modesFor(total) {
  const t = tierFor(total);
  if (t === 1) return [{ id: "solde", label: "Acompte + solde à réception", situations: 0 }];
  if (t === 2) return [
    { id: "solde", label: "Acompte + solde à réception", situations: 0 },
    { id: "1sit", label: "Acompte + 1 situation intermédiaire + solde", situations: 1 },
  ];
  // tier 3 — le donneur d'ordre choisit N ≥ 1
  return [{ id: "N-sit", label: "Acompte + N situations + solde", situations: null }];
}

/** Construit l'échéancier à partir des montants de situations saisis.
 * Renvoie ``{ lines, solde, ok, error }``. ``ok=false`` si les situations
 * dépassent le disponible ou si le total ne colle pas au montant exact. */
export function buildEcheancier(total, situationsMontants) {
  const acompte = round2(total * ACOMPTE_RATE);
  const sits = (situationsMontants || []).map((v) => Number(v) || 0);
  const sumSits = round2(sits.reduce((a, b) => a + b, 0));
  const solde = round2(total - acompte - sumSits);
  const lines = [
    { label: "Acompte", type: "acompte", montant_ht: acompte, ordre: 1 },
    ...sits.map((m, i) => ({ label: `Situation ${i + 1}`, type: "situation", montant_ht: round2(m), ordre: 2 + i })),
    { label: "Solde", type: "solde", montant_ht: solde, ordre: 2 + sits.length },
  ];
  const sum = round2(lines.reduce((a, l) => a + l.montant_ht, 0));
  let error = null;
  if (solde <= 0) error = "La somme des situations doit laisser un solde strictement positif.";
  else if (Math.abs(sum - total) > 0.01) error = "Le total ne correspond pas exactement au montant de la mission.";
  return { lines, solde, ok: !error, error };
}

function round2(x) { return Math.round(x * 100) / 100; }
